/*
 * StreamBank Admin v38.0
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {Fragment} from "react";
import {connect} from "react-redux";
import autobind from "autobind-decorator";
import {Row} from "react-bootstrap";

import {GenericModelViewer} from "./GenericModelViewer";
import {MutableList} from "./MutableList";
import {ConfirmationDialog} from "./ConfirmationDialog";
import {GuidUtils} from "../../Utils";

import {service as PermitService} from "../../Services/PermitService";

import {Permit} from "../../Models/StreamBank/Permit";
import {ConservationActionPermit} from "../../Models/StreamBank/ConservationActionPermit";
import {Glyphicon} from "./GlyphIcon";
import {StreamBankErrorDispatchEnhance} from "./Enhancements/StreamBankErrorDispatchEnhance";

@StreamBankErrorDispatchEnhance
class _StreamBankConservationActionPermitList extends MutableList {
    constructor(props) {
        super(props);

        this.state = {
            permit: {},
            displayPermit: false,
            isValid: false,
        }
    }

    @autobind
    getData() {
        const {customData} = this.props.AdditionalTypeProps.FieldDef;
        const data = super.getData();

        return data.filter(x => x?.Permit?.permitTypeId === customData?.permitTypeId);
    }

    @autobind
    getModel() {
        const permitId = this.state.permit.permitId;

        if(this.alteredPermitModel) {
            const conservationActionPermitFieldDef = this.alteredPermitModel
                .getFlattenedFieldDefs()
                .find((field) => field.key === "ConservationActionPermits");

            if(conservationActionPermitFieldDef.model?.defaults?.permitId === permitId) {
                return this.alteredPermitModel;
            }
        }

        const permitModel = new Permit();
        const alteredConservationActionPermitModel = new ConservationActionPermit();
        const conservationActionPermitFieldDefs = permitModel
            .getFlattenedFieldDefs()
            .find((field) => field.key === "ConservationActionPermits");

        alteredConservationActionPermitModel.defaults = {permitId};
        conservationActionPermitFieldDefs.model = alteredConservationActionPermitModel;

        return this.alteredPermitModel = permitModel;
    }

    @autobind
    filter(data, selectedModel) {
        const {customData} = this.props.AdditionalTypeProps.FieldDef;
        const excluded = this.getData().map(({permitId}) => permitId)
            .filter(permitId => permitId !== selectedModel?.permitId);

        return data.filter(x => x.permitTypeId === customData?.permitTypeId && !excluded.includes(x.permitId))
    }

    @autobind
    addItem(item) {
        const {customData} = this.props.AdditionalTypeProps.FieldDef;
        const organizationId = this.props.organizationId;

        this.setState({
            displayPermit: true,
            permit: {
                permitTypeId: customData?.permitTypeId,
                permitNumber: item,
                organizationId,
                ConservationActionPermits: [this.getTempConservationActionPermit()],
            }
        });
    }

    @autobind
    showPermit(permitId) {
        PermitService.GetPermit(permitId)
            .then(({result}) => {
                const {conservationActionId} = this.props.AdditionalTypeProps.RootData;

                if (!result.ConservationActionPermits.find(x => x.conservationActionId === conservationActionId )) {
                    result = {
                        ...result,
                        ConservationActionPermits: [...result.ConservationActionPermits, this.getTempConservationActionPermit(result.permitId)]
                    };
                }

                this.setState({displayPermit: true, permit: result});
            })
    }

    @autobind
    getTempConservationActionPermit(permitId = null) {
        const {conservationActionId, contextNodeId} = this.props.AdditionalTypeProps.RootData;

        return {
            _tempId: GuidUtils.Generate(),
            conservationActionId,
            contextNodeId,
            permitId,
        };
    }

    @autobind
    updatePermit(permit) {
        this.setState({permit});
    }

    @autobind
    save(data) {
        if (this.state.permit.permitId) {
            this.update(data);
        } else {
            this.create(data);
        }
    }

    @autobind
    create(data) {
        PermitService.CreatePermit(this.state.permit)
            .then(({result}) => {
                const updated = {
                    ...data,
                    permitId: result.permitId,
                    Permit: result,
                };
                this.props.UpdateItem(this.props.GetItemKey(data), updated);
                this.setState({displayPermit: false, permit: {}})
            })
            .catch((x) => {
                this.props.ErrorDispatch.SetError({
                    Code: x.xhr.status,
                    Message: x.message,
                    Title: x.xhr.statusText,
                    StackTrace: x.data.stackTrace
                });
            })
    }

    @autobind
    update(data) {
        PermitService.UpdatePermit(this.state.permit)
            .then(({result}) => {
                const unsetPermitId = {
                    ...data,
                    permitId: null,
                };

                const updated = {
                    ...data,
                    Permit: result,
                };

                // unsetPermitId then update to ensure permitId prop is changed in autocomplete component
                this.props.UpdateItem(this.props.GetItemKey(data), unsetPermitId);
                this.props.UpdateItem(this.props.GetItemKey(data), updated);
                this.setState({displayPermit: false, permit: {}})
            })
            .catch((x) => {
                this.props.ErrorDispatch.SetError({
                    Code: x.xhr.status,
                    Message: x.message,
                    Title: x.xhr.statusText,
                    StackTrace: x.data.stackTrace
                });
            })
    }

    @autobind
    updateValidation(validation, editorGuid) {
        for(const propertyName in validation) {
            if(!validation[propertyName].valid) {
                this.setState({isValid: false});
                return;
            }
        }

        this.setState({isValid: true});
    }

    @autobind
    getTitle() {
        if (!this.state.permit.permitId) {
            if (this.state.permit.permitNumber) {
                return `Create Permit - ${this.state.permit.permitNumber}`;
            }

            return 'Create Permit';
        }

        const permitOrg = this.state.permit?.PermitType?.PermitOrganization?.shortName || this.state.permit?.PermitType?.PermitOrganization?.name;
        const permitType = this.state.permit?.PermitType?.shortName || this.state.permit?.PermitType?.name;

        if (!this.props.Editable) {
            return `${permitOrg} ${permitType} ${this.state.permit.permitNumber}`;
        }

        return `Edit Permit - ${permitOrg} ${permitType} ${this.state.permit.permitNumber}`;
    }

    render() {
        const model = this.getModel();
        const surveyTypeId = this.props?.AdditionalTypeProps?.FieldDef?.customData?.surveyTypeId || null;
        const dialogDescription = <span><span className="dialog-bold">This Information is Read-Only.</span> Edit it by clicking one of the Conservation Actions and then clicking 'Edit' at the top of the Conservation Action Permits Page.</span>;

        return (
            <div>
                { this.props.children }
                {
                    this.getData() ?
                        this.getData().map((x) =>
                            <Fragment key={this.props.GetItemKey(x)}>
                                <ConfirmationDialog
                                    size="lg"
                                    Title={this.getTitle()}
                                    Body={
                                        this.state.displayPermit &&
                                        <GenericModelViewer
                                            Model={model}
                                            IsNewModel={this.state.permit.permitId === null}
                                            EditMode={this.props.Editable}
                                            Data={this.state.permit}
                                            Update={this.updatePermit}
                                            OnValidationUpdate={this.updateValidation}
                                            CustomData={{surveyTypeId}}
                                        />
                                    }
                                    CancelButtonMessage={this.props.Editable ? 'Cancel' : 'Dismiss'}
                                    OnClose={() => this.setState({displayPermit: false})}
                                    ConfirmationButtonMessage="Save Changes"
                                    Display={this.state.displayPermit}
                                    OnConfirm={this.props.Editable ? () => this.save(x) : null}
                                    DisableConfirmationButton={!this.state.isValid}
                                    DialogDescription={this.props.Editable ? null : dialogDescription}
                                />
                                <Row className='pop-row'>
                                    <div className="pop-col">
                                        <this.props.Type
                                            Key={this.props.GetItemKey(x)}
                                            Item={x}
                                            UpdateItem={(value) => this.props.UpdateItem(this.props.GetItemKey(x), value)}
                                            {...this.props.AdditionalTypeProps}
                                            CustomData={{
                                                filter: this.filter,
                                                addItem: this.addItem,
                                                showPermit: this.showPermit,
                                            }}
                                        />
                                    </div>
                                    {
                                        this.props.Editable &&
                                        <Fragment>
                                            {
                                                x.permitId &&
                                                <div className="edit-table">
                                                    <a onClick={() => this.showPermit(x.permitId)} className="sb-permit-edit-btn">
                                                        <Glyphicon glyph="pencil" />
                                                    </a>
                                                </div>
                                            }
                                            <div className="remove-table">
                                                <a className="pndlm-remove-control" onClick={() => this.props.RemoveItem(this.props.GetItemKey(x))}>
                                                    <Glyphicon glyph="remove-sign" />
                                                </a>
                                            </div>
                                        </Fragment>
                                    }
                                </Row>
                            </Fragment>
                        ) :
                        null
                }
                {
                    this.props.Editable ?
                        <Row className="pop-row">
                            <div className="add-table pop-add">
                                <a className="pndlm-remove-control" onClick={() => this.props.AddItem()}>
                                    <Glyphicon glyph="plus-sign" />
                                </a>
                            </div>
                        </Row> :
                        null
                }
            </div>
        );
    }
}

function mapStateToProps(state) {
    const {contextState} = state;
    return {
        organizationId: contextState.context?.organizationId,
    }
}

export const StreamBankConservationActionPermitList = connect(mapStateToProps)(_StreamBankConservationActionPermitList);
